import type { DhMode } from "../api/digitalHumanService";

import { CHARS_PER_SECOND, estSecondsFromChars } from "@/shared/lib/dhSpeech";

/** 口播语速(字/秒)与估秒函数的真源在 shared/lib/dhSpeech(作品卡投影也要用);此处原名重导出。 */
export { CHARS_PER_SECOND, estSecondsFromChars };
/**
 * 视频档文案上限的**回落值**:120 秒 × 4.5(放开自旧 68=15 秒)。1020 起真实上限按出片通道从服务端取
 * (model/dhVideoLimits `useDhVideoLimits`,新通道 1350 字≈5 分钟),本常量只在取不到时兜底。
 */
export const VIDEO_SCRIPT_MAX = 540;
/**
 * 照片生动版(flagship)的硬约束:上游 s2v 出片最长 20 秒。
 *
 * ★2026-08-13 测试机实据:输入框一直标着 800 字,而这一档实收 20 秒 ≈ 90 字 —— **差 9 倍**。
 *   当天 3 个人 5 次撞在这上面,而且全是**合成完配音之后**才被 ai 侧的 DH_AUDIO_TOO_LONG 拒:
 *   231 字→43 秒→失败;砍到 117 字→25 秒→还是失败;最后放弃出片改做纯配音。
 *   用户照 800 字写,注定撞墙,还得等一轮 TTS 才知道。所以上限必须**按档位给**,在输入时就限住。
 * 90 = FLAGSHIP_MAX_SEC × CHARS_PER_SECOND,与 ai 侧报错里的「精简到约 90 字内」同一口径。
 */
export const FLAGSHIP_MAX_SEC = 20;
export const FLAGSHIP_SCRIPT_MAX = FLAGSHIP_MAX_SEC * CHARS_PER_SECOND;
/**
 * 视频档倒放感软护栏提示(spec §4.2):底板短、文案长时 video_extension 会来回倒放补时长,
 * 底板越长越自然。PC/H5 共用一句,单一源防漂移。
 */
export const VIDEO_REVERSAL_HINT = "底板越长,长文案越自然(底板短、文案长时画面会来回倒放补足)";

/**
 * ★片型 = 创作第 1 步(2026-07-15 重排)。此前片型是**选形象的副作用**(选中形象 kind==="video"
 * 即落视频档),用户从没选择过它,只看见「口播设置」里档位/清晰度凭空消失 → 5 控件塌成 1、零解释。
 * 两档是能力/时长/成本不同的两条管线,差异必须在**选片型那刻**讲清 —— 后面控件的有无才读得懂
 * 是「这档本来就没有」,而不是「刚才还在怎么没了」。bullets 逐条对应真实约束,别写营销话。
 * PC/H5 共用,单一源防漂移。
 */
export type FilmType = "video" | "photo";

export interface FilmTypeCard {
  value: FilmType;
  label: string;
  summary: string;
  bullets: string[];
  recommended?: boolean;
}

/**
 * 片型卡文案。`resolutionChoice` = **该端是否真的让用户选清晰度**:PC 有下拉,H5 硬编码 480P。
 * 文案必须描述这个端真能做的事 —— H5 照抄「可选 480P / 720P」就是撒谎(用户在 H5 根本选不了)。
 * 其余文案两端同源,防漂移。
 */
export function filmTypeCards(opts: {
  resolutionChoice: boolean;
  /** 视频档最长时长文案(「120 秒」/「5 分钟」,按出片通道取;缺省老口径)。 */
  videoMaxLabel?: string;
}): FilmTypeCard[] {
  return [
    {
      value: "video",
      label: "真人视频换口播",
      summary: "用你的一段真人视频,只换台词对口型",
      bullets: [
        "最像真人,可用你的原声",
        `最长 ${opts.videoMaxLabel ?? "120 秒"}`,
        "清晰度跟随源视频",
      ],
      recommended: true,
    },
    {
      value: "photo",
      label: "照片开口说话",
      summary: "用一张清晰的单人正面照",
      bullets: [
        "不用拍视频",
        "最长 20 秒(生动版)",
        opts.resolutionChoice ? "可选 720 / 1080" : "高清 720",
      ],
    },
  ];
}

/**
 * 出片档位选项(persona 红线:label 不点名厂商,只讲语义/取向)。video 主推置顶;
 * 注:video 由「选/传视频形象」驱动(api 按形象 kind 决定管线)。
 * ★1019 照片省流版(longform)永久下架,选项已删;老作品的「照片省流版」展示名在 works mappers /
 *   任务详情页各自的表里,不读这里。
 */
export const DH_MODE_OPTIONS: { value: DhMode; label: string; hint: string }[] = [
  {
    value: "video",
    label: "真人换口播(推荐)",
    hint: "上传/录你的真人短视频,只换台词对口型,最像真人、可长口播",
  },
  { value: "flagship", label: "照片生动版", hint: "只需一张照片,带表情动作(较贵、偏短)" },
];

/**
 * 表演风格(火山方案 §10):只在生动版(flagship)有意义,控制人物「怎么演」;
 * 值与后端白名单逐字一致(api DhCreateGuards.PERFORMANCE_STYLES / ai volc_dh._STYLE_PROMPTS)。
 */
export const DH_PERFORMANCE_STYLE_OPTIONS: { value: string; label: string }[] = [
  { value: "professional", label: "专业口播" },
  { value: "warm", label: "亲和带货" },
  { value: "energetic", label: "活力种草" },
  { value: "calm", label: "沉稳讲解" },
];

/**
 * 表演风格入口开关。2026-08-24 P3 旗舰档默认切火山(OmniHuman 真单
 * task 5324954665984 已核账),风格 prompt 真吃了 —— 同批翻 true。
 * 万相期间必须关(引擎不吃风格,放出来是假交互)——回退万相时要一并翻回 false。
 */
export const DH_PERFORMANCE_STYLE_ENABLED = true;

/**
 * 分辨率档。value 是 api 契约值(计费映射 480P→720 档 / 720P→1080 档都认它),不许改;
 * label 是 P3 火山真实出片档(OmniHuman 480P 请求实出 720、720P 实出 1080)——
 * 万相期间的旧文案「标清 480P/高清 720P」只在回退时找回。
 */
export const DH_RESOLUTION_OPTIONS: { value: string; label: string }[] = [
  // 不写「(省积分)」:价钱由照片档单价卡(dh-mode-compare)按真价标。
  { value: "480P", label: "高清 720" },
  { value: "720P", label: "超清 1080" },
];

/**
 * 清晰度**契约值 → 用户看到的档名**(B2 #12,0927 K4):选择器里选的是「高清 720」,而底栏「清晰度 · 480P」、
 * 详情回显「480P」印的是契约值 —— 同一页两个名字,用户以为提交错了档。凡对用户展示一律走这里;
 * 契约值本身(提交 / 计费映射)不动。认不出的值原样返回,不编名。
 */
export function dhResolutionLabel(value: string | null | undefined): string {
  if (!value) return "";
  return DH_RESOLUTION_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/**
 * 预置音色**兜底常量**(值为 cosyvoice 合法音色 id,取自 gscq-ai SUPPORTED_VOICE_TONES)。
 *
 * ★真值源是后端 `GET /digital-human/preset-voices`(api `PresetVoiceCatalog`)——
 * 出片页/声音库页都读那个。本常量只在后端不可达/未 seed 时兜底,让音色选择器不塌;
 * wecom-h5 端与样片墙 admin 仍直接用它。E4(2026-07-23)补齐到与后端同 8 款,
 * 避免「声音库有 8 款、出片页只给 5 款」这种两边不一致(用户从声音库点「用 TA 出片」
 * 带过来的音色在出片页选不中 = 音色框空白)。改这里必须同步改 api PresetVoiceCatalog。
 */
export const DH_VOICE_OPTIONS: { value: string; label: string }[] = [
  { value: "longanhuan", label: "元气女声(默认)" },
  { value: "longxiaochun_v3", label: "知性女声" },
  { value: "longxiaoxia_v3", label: "沉稳权威女声" },
  { value: "longanyang", label: "阳光男声" },
  { value: "longshu_v3", label: "沉稳男声" },
  { value: "longyue_v3", label: "温暖磁性女声" },
  { value: "longhuhu_v3", label: "童声萌趣" },
  { value: "longyingjing_v3", label: "冷静专业女声" },
];

export const DH_DEFAULT_VOICE = "longanhuan";
